/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */

import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { default as SparkMD5 } from 'spark-md5';
import { ShowInfo } from '../../../core/state/notification/notification.actions';
import { DashGisUtil } from '../../../modules/shared/dash-gis-util';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { RestService } from '../../../modules/shared/services/rest.service';
import { DirectUrlResponse } from '../../../types/FileDownload';
import { ApiSuccessResponse } from '../../../types/Rest';
import {
    AddBulkUploadFile,
    InitializeBulkUpload,
    PostBulkUploadFile,
    RemoveBulkUploadFile,
    RequestBulkUploadDirectUri,
    ResetBulkUploadFiles,
    SendBulkUploadFiles,
    SendBulkUploadFilesDirect,
    SetBulkUploadFeatureName,
    UpdateBulkUploadFileStatus
} from './bulk-upload.actions';
import { BulkUploadConfiguration, BulkUploadFeatureSelect, BulkUploadModel, PendingBulkUploadFile } from './bulk-upload.model';

@State<BulkUploadModel>({
    name: 'bulkUpload',
    defaults: {
        initialized: false,
        configuration: {
            maxUploadCount: 5,
            features: null,
            useDirectUrl: false,
        },
        pendingFiles: [],
        featureName: '',
    },
})
@Injectable()
export class BulkUploadState {
    private defaultState: BulkUploadModel;

    constructor(private logger: LoggerService, private rest: RestService) {}

    @Action(InitializeBulkUpload)
    initializeBulkUpload({ setState, getState }: StateContext<BulkUploadModel>) {
        if (getState().initialized) {
            return;
        }
        return this.rest.apiGet<BulkUploadConfiguration>('storage/bulk/config').pipe(
            tap((config: BulkUploadConfiguration) => {
                setState(
                    patch<BulkUploadModel>({
                        initialized: true,
                        configuration: config,
                    })
                );
                this.defaultState = getState();
            }),
            catchError((err) => {
                this.logger.error(err);
                return of(err);
            }),
        );
    }

    @Action(AddBulkUploadFile)
    addBulkUploadFile({ getState, setState, dispatch }: StateContext<BulkUploadModel>, action: AddBulkUploadFile) {
        // validate that name matches the pattern for the selected feature type
        const state = getState();
        const config = state.configuration.features[state.featureName];
        const pattern = config.pattern;
        const isLessThanMaxFiles = state.pendingFiles.length < state.configuration.maxUploadCount;
        const isAlreadyIncluded = state.pendingFiles.map((f) => f.file.name).includes(action.file.name);
        if (isLessThanMaxFiles && !isAlreadyIncluded) {
            const re = new RegExp(pattern);
            const testFilename = action.file.name.substring(0, action.file.name.lastIndexOf('.'));
            if (!re.test(testFilename)) {
                return dispatch(new ShowInfo(`Filename [${action.file.name}] does not match the expected pattern.`));
            }
            const spark = new SparkMD5.ArrayBuffer();
            const pending: PendingBulkUploadFile = {
                file: action.file,
                valid: re.test(action.file.name),
                status: 'Loading',
            };
            setState(patch({ pendingFiles: append([pending]) }));
            this.readFileByChunks(action.file).subscribe({
                next: (buffer) => spark.append(buffer),
                complete: () => {
                    const md5Hash = spark.end();
                    return setState(
                        patch({
                            pendingFiles: updateItem<PendingBulkUploadFile>(
                                (item) => item.file.name === pending.file.name,
                                patch({
                                    md5: md5Hash,
                                    status: 'Pending',
                                })
                            ),
                        })
                    );
                },
            });
        }
    }

    @Action(RemoveBulkUploadFile)
    removeBulkUploadFile({ setState }: StateContext<BulkUploadModel>, action: RemoveBulkUploadFile) {
        setState(
            patch({ pendingFiles: removeItem<PendingBulkUploadFile>((pending) => pending.file.name === action.name) })
        );
    }

    @Action(SetBulkUploadFeatureName)
    setBulkUploadFeatureName({ setState }: StateContext<BulkUploadModel>, action: SetBulkUploadFeatureName) {
        setState(patch({ featureName: action.featureName }));
    }

    @Action(ResetBulkUploadFiles)
    resetBulkUploadFiles({ setState }: StateContext<ResetBulkUploadFiles>) {
        setState(this.defaultState);
    }

    @Action(SendBulkUploadFiles)
    sendBulkUploadFiles({ getState, dispatch }: StateContext<BulkUploadModel>) {
        const state = getState();
        const useDirect = state.configuration.useDirectUrl;
        const actions = [];
        const files = state.pendingFiles;
        files.forEach((file) => {
            actions.push(useDirect ? new RequestBulkUploadDirectUri(file) : new PostBulkUploadFile(file));
        });
        return dispatch(actions);
    }

    @Action(PostBulkUploadFile)
    postBulkUploadFiles({ getState, dispatch }: StateContext<BulkUploadModel>, action: PostBulkUploadFile) {
        const state = getState();
        const file = action.payload;
        return dispatch(
            new UpdateBulkUploadFileStatus({
                file,
                status: 'Uploading',
            })
        ).pipe(
            mergeMap(() => this.createBulkUploadRequest(state.featureName, file)),
            map((result) => ({
                success: result,
                file,
            })),
            tap((result) => {
                dispatch(
                    new UpdateBulkUploadFileStatus({
                        status: result.success ? 'Uploaded' : 'Failed',
                        file: result.file,
                    })
                );
            })
        );
    }

    @Action(RequestBulkUploadDirectUri)
    requestUris({ getState, dispatch }: StateContext<BulkUploadModel>, action: RequestBulkUploadDirectUri) {
        const state = getState();
        const file = action.payload;
        dispatch(
            new UpdateBulkUploadFileStatus({
                file,
                status: 'Uploading',
            })
        );
        return this.rest
            .apiPost<DirectUrlResponse>('storage/bulk/directWrite', {
                feature: state.featureName,
                filename: file.file.name,
                hash: file.md5,
                mime: file.file.type,
                size: file.file.size,
            })
            .pipe(
                mergeMap((response: DirectUrlResponse | ApiSuccessResponse) => {
                    const urlResponse = response as DirectUrlResponse;
                    const err = response as ApiSuccessResponse;
                    if (urlResponse?.url) {
                        return dispatch(
                            new SendBulkUploadFilesDirect({
                                file,
                                urlObject: urlResponse,
                            })
                        );
                    } else {
                        return throwError(err);
                    }
                }),
                catchError((err) => {
                    this.logger.warn(err);
                    return of({
                        success: false,
                        message: err.message,
                        file: file,
                    });
                }),
                tap((result) => {
                    if (result && result.success === false) {
                        dispatch(
                            new UpdateBulkUploadFileStatus({
                                status: 'Failed',
                                error: result.message,
                                file: result.file,
                            })
                        );
                    }
                })
            );
    }

    @Action(SendBulkUploadFilesDirect)
    sendFilesDirect({ dispatch }: StateContext<BulkUploadModel>, action: SendBulkUploadFilesDirect) {
        return DashGisUtil.readFile(action.payload.file.file).pipe(
            mergeMap((bytes) =>
                this.rest.put(action.payload.urlObject.url, null, bytes, {
                    headers: action.payload.urlObject.headers,
                    observe: 'response',
                    responseType: 'text',
                })
            ),
            map((result: HttpResponse<any>) => ({
                success: result.status === 201,
                message: null,
                file: action.payload.file,
            })),
            catchError((err) => {
                this.logger.warn(err);
                return of({
                    success: false,
                    message: err.message,
                    file: action.payload.file,
                });
            }),
            tap((result) => {
                dispatch(
                    new UpdateBulkUploadFileStatus({
                        status: result.success ? 'Uploaded' : 'Failed',
                        error: result.message,
                        file: result.file,
                    })
                );
            })
        );
    }

    @Action(UpdateBulkUploadFileStatus)
    updateStatus({ getState, setState }: StateContext<BulkUploadModel>, action: UpdateBulkUploadFileStatus) {
        const index = getState().pendingFiles.findIndex((f) => f.md5 === action.payload.file.md5);
        return setState(
            patch({
                pendingFiles: updateItem<PendingBulkUploadFile>(
                    index,
                    patch({
                        status: action.payload.status,
                        error: action.payload.error,
                    })
                ),
            })
        );
    }

    private createBulkUploadRequest(feature: string, file: PendingBulkUploadFile) {
        const postData = new FormData();
        postData.append('feature', feature);
        postData.append(`file`, file.file, file.file.name);

        return this.rest.apiPost<boolean>('/storage/bulk/store', postData, {}, { skipErrorHandler: true }).pipe(
            catchError((err) => {
                this.logger.error(err);
                return of(false);
            })
        );
    }

    private readFileByChunks(file: File): Observable<ArrayBuffer> {
        const chunkSize = 2097152;
        let currentChunk = 0;
        const chunkCount = Math.ceil(file.size / chunkSize);
        const subject = new Subject<ArrayBuffer>();
        const reader = new FileReader();

        const readNext = () => {
            const start = currentChunk * chunkSize;
            const end = start + chunkSize >= file.size ? file.size : start + chunkSize;
            reader.readAsArrayBuffer(file.slice(start, end));
        };

        reader.onload = (evt) => {
            subject.next(evt.target.result as ArrayBuffer);
            currentChunk++;

            if (currentChunk < chunkCount) {
                readNext();
            } else {
                subject.complete();
            }
        };

        readNext();

        return subject.asObservable();
    }

    @Selector()
    static maxUploadCount(state: BulkUploadModel) {
        return state.configuration.maxUploadCount;
    }

    @Selector()
    static featureNames(state: BulkUploadModel): BulkUploadFeatureSelect[] {
        return Object.keys(state.configuration.features).map((name) => ({
            name: name,
            label: state.configuration.features[name].label,
        }));
    }

    @Selector()
    static featureName(state: BulkUploadModel) {
        return state.featureName;
    }

    @Selector()
    static feature(state: BulkUploadModel) {
        return state.configuration.features[state.featureName];
    }

    @Selector()
    static pendingFiles(state: BulkUploadModel) {
        return state.pendingFiles;
    }
}
